import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { logger } from "@/lib/logger";
import { useGoogleDriveCache } from "@/lib/contexts/GoogleDriveCacheContext";

import type {
  DriveListResult,
  UseDriveBrowserParams,
} from "./google-drive-browser-types";
import {
  buildAuthHeaders,
  buildBrowseQueryParams,
  parseDriveErrorMessage,
} from "./google-drive-browser-internals";

export type {
  GoogleDriveFile,
  GoogleSharedDrive,
  UseDriveBrowserParams,
  DriveListResult,
} from "./google-drive-browser-types";

export {
  useCreateGoogleDriveFolder,
  useUploadGoogleDriveFile,
  useDeleteGoogleDriveFile,
} from "./use-google-drive-browser-mutations";

/**
 * Hook to browse Google Drive files and folders with intelligent caching
 */
export const useGoogleDriveBrowser = (params: UseDriveBrowserParams = {}) => {
  const { session } = useUniversalAuth();
  const cache = useGoogleDriveCache();
  const {
    enabled = true,
    parentId,
    driveId,
    action = "list",
    email,
    isSecondAccount = false,
    pageToken,
    pageSize,
    query,
  } = params;

  const cacheKey = {
    action,
    parentId,
    driveId,
    email,
    isSecondAccount,
    pageToken,
    pageSize,
    query,
  };

  return useQuery({
    queryKey: ["google-drive-browser", cacheKey],
    queryFn: async (): Promise<DriveListResult> => {
      logger.info("🔍 Google Drive Browser Query Starting...", { params });

      const cachedEntry = cache.get(cacheKey);
      if (cachedEntry) {
        logger.info("🚀 Google Drive Browser Query: Using cached data", {
          cacheKey,
          timestamp: new Date(cachedEntry.timestamp).toISOString(),
          expiresAt: new Date(cachedEntry.expiresAt).toISOString(),
        });
        return cachedEntry.data;
      }

      const token = session?.access_token;
      if (!token) {
        logger.error("🔍 Google Drive Browser Query: No auth token");
        throw new Error("No authentication token");
      }

      const queryParams = buildBrowseQueryParams({
        parentId,
        driveId,
        action,
        email,
        isSecondAccount,
        pageToken,
        pageSize,
        query,
      });

      logger.info("🔍 Google Drive Browser Query: Making API call with params:", {
        params: queryParams.toString(),
      });

      const response = await fetch(`/api/unifiedtime/getDrive/browse?${queryParams.toString()}`, {
        headers: buildAuthHeaders(token),
      });

      if (!response.ok) {
        const errorMessage = await parseDriveErrorMessage(
          response,
          `HTTP ${response.status}: ${response.statusText}`
        );
        logger.error("🔍 Google Drive Browser Query: API error", { errorMessage });
        throw new Error(errorMessage);
      }

      const result: DriveListResult = await response.json();
      logger.info("🔍 Google Drive Browser Query: API response received:", {
        filesCount: result.files?.length || 0,
        drivesCount: result.drives?.length || 0,
        hasAccess: result.hasAccess,
      });

      if (result.hasAccess && !result.error) {
        cache.set(cacheKey, result);
        logger.info("💾 Google Drive Browser Query: Data cached", { cacheKey });
      }

      return result;
    },
    enabled: enabled && !!session?.access_token,
    staleTime: 2 * 60 * 1000, // 2 minutes - longer stale time since we have custom caching
    gcTime: 10 * 60 * 1000, // 10 minutes - longer garbage collection
    retry: 1, // Reduce retry attempts to 1
    refetchOnMount: false, // Don't refetch on mount since we have cache
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to list shared drives for an account
 */
export const useGoogleSharedDrives = (
  params: {
    enabled?: boolean;
    email?: string;
    isSecondAccount?: boolean;
  } = {}
) => {
  return useGoogleDriveBrowser({
    ...params,
    action: "drives",
  });
};

/**
 * Hook to list files in a specific folder or drive
 */
export const useGoogleDriveFiles = (
  params: {
    enabled?: boolean;
    parentId?: string;
    driveId?: string;
    email?: string;
    isSecondAccount?: boolean;
    pageSize?: number;
  } = {}
) => {
  return useGoogleDriveBrowser({
    ...params,
    action: "list",
  });
};

export const useGoogleFolderInfo = (params: { enabled?: boolean; id?: string } = {}) => {
  const { session } = useUniversalAuth();
  const { enabled = true, id } = params;
  return useQuery({
    queryKey: ["google-drive-folder-info", id],
    queryFn: async () => {
      const token = session?.access_token;
      if (!token) throw new Error("No authentication token");
      if (!id) throw new Error("No folder id");
      const resp = await fetch(
        `/api/unifiedtime/getDrive/folderInfo?id=${encodeURIComponent(id)}`,
        { headers: buildAuthHeaders(token, false) }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.json();
    },
    enabled: enabled && !!session?.access_token && !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for cache management and invalidation
 */
export const useGoogleDriveCacheControl = () => {
  const cache = useGoogleDriveCache();

  return {
    invalidateFolder: (parentId: string, driveId?: string, isSecondAccount?: boolean) => {
      cache.invalidatePattern({
        action: "list",
        parentId,
        driveId,
        isSecondAccount,
      });
    },

    invalidateDrive: (driveId: string, isSecondAccount?: boolean) => {
      cache.invalidatePattern({
        driveId,
        isSecondAccount,
      });
    },

    invalidateAccount: (isSecondAccount: boolean) => {
      cache.invalidatePattern({ isSecondAccount });
    },

    invalidateSharedDrives: (isSecondAccount?: boolean) => {
      cache.invalidatePattern({
        action: "drives",
        isSecondAccount,
      });
    },

    forceRefresh: (params: UseDriveBrowserParams) => {
      const cacheKey = {
        action: params.action || "list",
        parentId: params.parentId,
        driveId: params.driveId,
        email: params.email,
        isSecondAccount: params.isSecondAccount || false,
      };
      cache.invalidate(cacheKey);
    },

    clearAll: cache.clear,

    getCacheInfo: cache.getCacheInfo,

    setConfig: cache.setConfig,
  };
};
